import type { ReactElement } from "react";
import { Zap } from "lucide-react";
import { Button } from "@primitives/Button";
import { ACTION_SHORTCUTS } from "./actionShortcuts";
import styles from "./NextButton.module.css";

/**
 * NextButton: the triage hero. One press hands the user the next most
 * important due item. Filled primary, `Zap`, shortcut `N`. A slow, finite
 * accent breath plays while idle (three cycles, `NextButton.module.css`).
 *
 * `labeled` shows the word and the `N` hint; otherwise the control is
 * icon-only with the same tooltip. While `pending` the hero shows the busy
 * spinner and does not breathe.
 */

export interface NextButtonProps {
  readonly onNext: () => void;
  readonly pending: boolean;
  /** Default `true`. */
  readonly labeled?: boolean;
  readonly testId?: string;
}

const NEXT_TOOLTIP = `Get the next most important work item (${ACTION_SHORTCUTS.next})`;

export function NextButton({
  onNext,
  pending,
  labeled = true,
  testId,
}: NextButtonProps): ReactElement {
  return (
    <span className={styles.hero} data-idle={pending ? undefined : ""}>
      <Button
        variant="primary"
        size="md"
        iconOnly={!labeled}
        busy={pending}
        onClick={onNext}
        title={NEXT_TOOLTIP}
        aria-label={labeled ? undefined : "Next"}
        aria-keyshortcuts={ACTION_SHORTCUTS.next}
        data-testid={testId ?? "next-button"}
      >
        <Zap size={labeled ? 16 : 18} strokeWidth={2.25} aria-hidden="true" />
        {labeled && (
          <>
            Next
            <kbd aria-hidden="true" className={styles.kbd}>
              {ACTION_SHORTCUTS.next}
            </kbd>
          </>
        )}
      </Button>
    </span>
  );
}
